import {
    h, ComponentChildren, ComponentType, JSX, Ref
} from "preact";

import classname from "../../../foundation/utils/classname";
import IconProps from "../../Icons/IconProps";
import IconLoading from "../Icons/IconLoading";

import styles from "./Button.module.css";

type NativeButtonProps = Omit<JSX.HTMLAttributes<HTMLButtonElement>, "icon">;

enum Variant {
    Primary = "primary",
    Secondary = "secondary",
    Ninja = "ninja",
    Danger = "danger"
}

interface Props extends NativeButtonProps {
    buttonRef?: Ref<HTMLButtonElement>;
    textClass?: string; /* Class of the text style. Example: "body-1", "caption-1 medium" */
    variant?: Variant;
    active?: boolean;
    icon?: ComponentType<IconProps>;
    children?: ComponentChildren;
    isPending?: boolean;
    onClick?: (event: MouseEvent) => void;
}

function Button({
    buttonRef,
    textClass = "body-1 medium",
    variant = Variant.Primary,
    active = false,
    icon: Icon,
    children,
    isPending,
    onClick,
    // native props
    class: cssClass,
    disabled = false,
    readOnly = false,
    type = "button",
    ...nativeButtonProps
}: Props) {
    const hasIconLeft = !!Icon;

    function handleClick(event: MouseEvent) {
        if (readOnly) {
            return;
        }

        onClick?.(event);
    }

    return (
        <button
            ref={buttonRef}
            type={type}
            disabled={disabled || isPending}
            {...nativeButtonProps}
            class={classname(textClass, styles.button, cssClass, styles[variant], {
                [styles.hasIconLeft]: hasIconLeft,
                [styles.readOnly]: readOnly,
                [styles.active]: active,
                [styles.pending]: isPending
            })}
            readOnly={readOnly}
            onClick={handleClick}>
            {Icon && (
                <div class={styles.iconLeft}>
                    <Icon/>
                </div>
            )}
            {children}
            {isPending && (
                <div class={styles.pendingContainer}>
                    <IconLoading class="spinning"/>
                </div>
            )}
        </button>
    );
}

export default Button;
export {
    Props as ButtonProps,
    Variant as ButtonVariant
};
